import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from 'config/axiosConfig';
import Feed from 'components/Entities/Feed';
import TeamProfilePlate from './TeamProfilePlate';
import { Team, User } from 'util/constants';
import { useUser } from 'util/UserContext';

// Import the new reusable scrollable tabs
import ScrollableTabsList from 'ui/ScrollableTabsList';
import { capitalizeFirstLetter } from 'util/textUtils';
import { setDocumentTitle, setEntityOg } from 'util/ogMetadataUtils';
import { AxiosError } from 'axios';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import ScrollHideContainer from 'ui/ScrollHideContainer';
import { apiRoutes } from "util/apiRoutes";

const TeamProfile: React.FC = () => {
    const { awaitingUser } = useUser();
    const { teamname, tab } = useParams<{ teamname: string; tab?: string }>();
    const [ creatorUsername, setCreatorUsername ] = useState<string>('');
    const [selectedTab, setSelectedTab] = useState<string>(tab || 'overview');
    const [teamnameActive, setTeamnameActive] = useState<Team | any | null>(null);
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);

    setDocumentTitle(`Partea - ${teamname} - ${capitalizeFirstLetter(selectedTab)}`);

    useEffect(() => {
        if (tab) {
            setSelectedTab(tab);
        } else {
            setSelectedTab('overview');
        }
    }, [tab]);

    useEffect(() => {
        const fetchTeamname = async () => {
            if (awaitingUser) {
                return;
            }
            try {
                const response = await api.get<Team>(apiRoutes.team.byName(teamname || ""));
                setTeamnameActive(response.data);
                // Update OG meta for team page
                setEntityOg({
                    entityType: 'team',
                    entity: response.data as any,
                    urlPath: `/partea/${encodeURIComponent(String(teamname))}`,
                });
                if (response.data.properties_public?.creator_id) {
                    const creator = await api.get<User>(apiRoutes.user.byId(response.data.properties_public?.creator_id || ""));
                    setCreatorUsername(creator.data.username);
                }
                setLoading(false);
            } catch (error) {
                setLoading(false);
                if (error instanceof AxiosError && error.response?.status === 404) {
                    setError(`404: Partea with teamname: ${teamname} does not exist or is inactive.`);
                } else {
                    console.error(`Error fetching Partea with teamname: ${teamname}`, error);
                    setError(`Something went wrong fetching Partea with teamname: ${teamname}`);
                }
            }
        };
        fetchTeamname();
    }, [teamname, awaitingUser]);

    if (!loading && !teamnameActive && !creatorUsername) {
        return <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, no one's using that Partea name.`} errorSubtext={`The Partea may be banned or inactive, or the name is incorrect.`} buttonText={`Look Somewhere Else`} />;
    }
    
    if (!loading && error) {
        return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry!, no one's using that Partea name.`} errorSubtext={`The Partea may be banned or inactive, or the name is incorrect.`} buttonText={`Look Somewhere Else`} /> : <ServerErrorPage />;
    }

    // Build the array of tabs to pass into ScrollableTabsList
    const tabsData = [
        {
            label: 'Overview',
            route: `/partea/${teamname}/overview`,
            value: 'overview',
        },
        {
            label: 'Posts',
            route: `/partea/${teamname}/posts`,
            value: 'posts',
        },
        {
            label: 'Comments',
            route: `/partea/${teamname}/comments`,
            value: 'comments',
        },
    ];

    return (
        <div className="py-0 relative">
            {/* Team Profile Plate */}
            {teamnameActive && <TeamProfilePlate team={teamnameActive as any} isProfilePage={true}/>}

            <ScrollHideContainer 
                className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
                hideOffset={57}
                threshold={56}
            >
                {/* Reusable Scrollable Tabs */}
                <div className="p-2">
                    <ScrollableTabsList
                        key={`${teamname}-tabs`} 
                        pageTitle={`Partea - ${teamname}`}
                        tabs={tabsData}
                        selectedTab={selectedTab}
                        onTabChange={() => {}}
                    />
                </div>
            </ScrollHideContainer>

            {/* Tabs Content */}
            <div className="py-0 relative">
                {selectedTab === 'overview' && (
                    <Feed
                        actorId={teamnameActive?.id}
                        entityTypes={[
                            'impact',
                            'solution',
                            'comment'
                        ]}
                        hideTabList
                        isActorPage={true}
                    />
                )}
                {selectedTab === 'posts' && (
                    <Feed
                        actorId={teamnameActive?.id}
                        entityTypes={['impact', 'solution']}
                        hideTabList
                        isActorPage={true}
                    />
                )}
                {selectedTab === 'comments' && (
                    <Feed
                        actorId={teamnameActive?.id}
                        entityTypes={['comment']}
                        hideTabList
                        isActorPage={true}
                    />
                )}
            </div>
        </div>
    );
};

export default TeamProfile; 